/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

// Component imports
import { Button, Col, Row, Tooltip, Typography } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import AnalyticsTable from '../Tables/AnalyticsTable';

// Misc imports
import { NAV_BOT } from '../../constants/navigation';
import { ACTIVITY_TYPES } from '../../constants/main';
import replaceUrlParams from '../../utils/string/replaceUrlParams';
import exportAnalyticsData from '../../utils/misc/exportAnalyticsData';

const UserFeedbackAnalysis = ({ data = [], sectionTitle = '', isLoading = true, renderOnlyTable = false, icon = null }) => {
  const { t } = useTranslation();
  const { orgId } = useParams();

  const tableConfig = {
    tableData: data,
    isDescriptionTable: false,
    isTableLoading: isLoading,
    emptyLines: 0,
    noScroll: data?.length < 8,
    scroll: data?.length > 8 ? 518 : null,
    tableColumns: [
      {
        title: t('User Input'),
        dataIndex: 'userInput',
        width: '35%',
        align: 'left',
        ellipsis: true,
        sorter: (a, b) => a.userInput?.localeCompare(b.userInput)
      },
      {
        title: t('Agent Response'),
        dataIndex: 'botResponse',
        width: '30%',
        align: 'left',
        ellipsis: true,
        sorter: (a, b) => a.botResponse?.localeCompare(b.botResponse)
      },
      {
        title: t('Flow/Automated Answer'),
        dataIndex: 'displayName',
        link: true,
        width: '35%',
        align: 'center',
        ellipsis: true,
        sorter: (a, b) => a.displayName?.localeCompare(b.displayName),
        render: (text, record) => {
          if (record.type === ACTIVITY_TYPES.AUTOMATED_ANSWERS) {
            return (
              <Link
                to={replaceUrlParams(NAV_BOT, { tenantId: record.tenantId, orgId }) + '?view=behavior&tab=automated-answers'}
                target="_blank"
                rel="noopener noreferrer"
              >
                {text}
              </Link>
            );
          }

          return (
            <Link
              to={replaceUrlParams(NAV_BOT, { tenantId: record.tenantId, orgId }) + `?view=behavior&tab=activities&activity=${record?.id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {text}
            </Link>
          );
        }
      }
    ]
  };

  return (
    <Fragment>
      {
        renderOnlyTable ? (
          <AnalyticsTable
            tableData={tableConfig.tableData}
            isDescriptionTable={tableConfig.isDescriptionTable}
            isTableLoading={tableConfig.isTableLoading}
            emptyLines={tableConfig.emptyLines}
            tableColumns={tableConfig.tableColumns}
            noScroll={tableConfig.noScroll}
          />
        ) : (
          <Fragment>
            <Row justify="space-between">
              <Col className="mb-6 position-relative">
                <Typography.Title level={4} className="mb-6">
                  <Typography.Text className="mr-2">{sectionTitle}</Typography.Text>
                  {!!icon && icon }
                </Typography.Title>
              </Col>
              <Col>
                <Tooltip title={tableConfig.tableData.length === 0 ? t('There are no analytics to export') : t('Export as CSV')}>
                  <Button
                    type="text"
                    size="large"
                    onClick={() => exportAnalyticsData(tableConfig, 'user_feedback', sectionTitle)}
                    loading={tableConfig.isTableLoading}
                    disabled={tableConfig.tableData.length === 0}
                    icon={<DownloadOutlined />}
                  />
                </Tooltip>
              </Col>
            </Row>

            <AnalyticsTable
              tableData={tableConfig.tableData}
              isDescriptionTable={tableConfig.isDescriptionTable}
              isTableLoading={tableConfig.isTableLoading}
              emptyLines={tableConfig.emptyLines}
              tableColumns={tableConfig.tableColumns}
              noScroll={tableConfig.noScroll}
            />
          </Fragment>
        )
      }
    </Fragment>
  );
};

UserFeedbackAnalysis.propTypes = {
  data: PropTypes.array,
  sectionTitle: PropTypes.string,
  seriesName: PropTypes.string,
  isLoading: PropTypes.bool,
  renderOnlyTable: PropTypes.bool,
  icon: PropTypes.any
};

export default UserFeedbackAnalysis;
